import {ChangeDetectionStrategy, Component, effect, inject, viewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {
  ProductTableComponent,
  ProductTableSelectionChange,
} from '../../../common/components/product-table/product-table.component';
import {getTableRowId} from '../../../common/components/product-table/product-table-row';
import {ProductQueryParams} from '../../../models';
import {CatalogDetailActions} from '../store/catalog-detail.actions';
import {
  selectCatalogBundleSelectionBundles,
  selectCatalogBundleSelectionError,
  selectCatalogBundleSelectionLoading,
  selectCatalogBundleSelectionPaginationOptions,
  selectCatalogBundleSelectionSaveError,
  selectCatalogBundleSelectionSaveStatus,
  selectCatalogBundleSelectionSaving,
  selectCatalogBundleSelectionSearchQuery,
  selectCatalogBundleSelectionSelectedCount,
  selectCatalogBundleSelectionSelectedIds,
  selectCatalogBundleSelectionSortBy,
  selectCatalogBundleSelectionSortDirection,
} from '../store/catalog-detail.selectors';

@Component({
  selector: 'choose-catalog-bundles-modal',
  templateUrl: './choose-catalog-bundles-modal.component.html',
  styleUrl: './choose-catalog-bundles-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ProductTableComponent, SeFeAlertModule, SeFeButtonModule, SeFeModalModule],
})
export class ChooseCatalogBundlesModalComponent {
  readonly modal = viewChild.required<SeFeModalComponent>('modal');

  private readonly store = inject(Store);

  readonly bundles = toSignal(this.store.select(selectCatalogBundleSelectionBundles), {requireSync: true});
  readonly loading = toSignal(this.store.select(selectCatalogBundleSelectionLoading), {requireSync: true});
  readonly error = toSignal(this.store.select(selectCatalogBundleSelectionError), {requireSync: true});
  readonly query = toSignal(this.store.select(selectCatalogBundleSelectionSearchQuery), {requireSync: true});
  readonly sortBy = toSignal(this.store.select(selectCatalogBundleSelectionSortBy), {requireSync: true});
  readonly sortDirection = toSignal(this.store.select(selectCatalogBundleSelectionSortDirection), {
    requireSync: true,
  });
  readonly paginationOptions = toSignal(this.store.select(selectCatalogBundleSelectionPaginationOptions), {
    requireSync: true,
  });
  readonly selectedBundleIds = toSignal(this.store.select(selectCatalogBundleSelectionSelectedIds), {
    requireSync: true,
  });
  readonly selectedCount = toSignal(this.store.select(selectCatalogBundleSelectionSelectedCount), {
    requireSync: true,
  });
  readonly saving = toSignal(this.store.select(selectCatalogBundleSelectionSaving), {requireSync: true});
  readonly saveError = toSignal(this.store.select(selectCatalogBundleSelectionSaveError), {requireSync: true});

  private readonly saveStatus = toSignal(this.store.select(selectCatalogBundleSelectionSaveStatus), {
    requireSync: true,
  });

  constructor() {
    effect(() => {
      if (this.saveStatus() === 'success') {
        this.modal().close();
      }
    });
  }

  open(focusTarget: EventTarget | null): void {
    this.store.dispatch(CatalogDetailActions.openBundleChooser());
    this.modal().open(focusTarget);
  }

  close(): void {
    this.modal().close();
  }

  modalClosed(): void {
    this.store.dispatch(CatalogDetailActions.closeBundleChooser());
  }

  queryChanged(queryParams: ProductQueryParams): void {
    this.store.dispatch(CatalogDetailActions.bundleChoicesQueryChanged({queryParams}));
  }

  selectionChanged({currentPageIds, selectedRows}: ProductTableSelectionChange): void {
    this.store.dispatch(
      CatalogDetailActions.bundleChoicesSelectionChanged({
        currentPageBundleIds: currentPageIds,
        selectedBundleIds: selectedRows.map(getTableRowId).filter((id): id is string => !!id),
      })
    );
  }

  addSelectedBundles(): void {
    this.store.dispatch(CatalogDetailActions.addSelectedBundles());
  }
}
